// ─────────────────────────────────────────────────────────────
// src/pages/TaxCalculator/index.jsx
// Page container – composes UI components and wires up hooks.
// No business logic, no raw tax math: only orchestration.
// ─────────────────────────────────────────────────────────────

import React, { useContext, useState } from "react";
import { Calculator } from "lucide-react";

import { ThemeContext } from "../theme";
import { LanguageContext } from "../LanguageContext";
import translations        from "../../utils/translations";

import useScreenSize              from "../../hooks/useScreenSize";
import useIndividualCalculator    from "../../hooks/useIndividualCalculator";
import useBulkCalculator          from "../../hooks/useBulkCalculator";
import useTaxCalculatorStyles     from "../../hooks/useTaxCalculatorStyles";

import ModeSelector           from "./components/ModeSelector";
import CalculationModeToggle  from "./components/CalculationModeToggle";
import IndividualForm         from "./components/IndividualForm";
import ResultsCard            from "./components/ResultsCard";
import BulkUploader           from "./components/BulkUploader";

const TaxCalculatorPage = () => {
  const { theme, currentTheme } = useContext(ThemeContext);
  const { language }            = useContext(LanguageContext);

  const t      = translations[language];
  const isRTL  = language === "ar";

  const screenSize = useScreenSize();

  // ── Shared UI state ────────────────────────────────────────
  const [processingMode,   setProcessingMode]   = useState("individual");
  const [calculationMode,  setCalculationMode]  = useState("netFromGross");

  // ── Feature hooks ──────────────────────────────────────────
  const individual = useIndividualCalculator(calculationMode, t);
  const bulk       = useBulkCalculator(calculationMode, language, t);

  // ── Derived styles ─────────────────────────────────────────
  const styles = useTaxCalculatorStyles(
    theme,
    currentTheme,
    screenSize,
    isRTL,
    bulk.processing,
  );

  // ── Handlers ───────────────────────────────────────────────
  const handleSelectProcessingMode = (mode) => {
    setProcessingMode(mode);
    individual.handleReset();
    bulk.resetBulk();
  };

  const handleSelectCalculationMode = (mode) => {
    setCalculationMode(mode);
    individual.handleReset();
    bulk.resetBulk();
  };

  // Card hover helpers (desktop only)
  const handleMouseEnter = (e) => {
    if (screenSize.isMobile) return;
    e.currentTarget.style.boxShadow  = theme === "dark"
      ? "0 12px 40px rgba(0,0,0,0.4)"
      : "0 12px 40px rgba(0,0,0,0.12)";
    e.currentTarget.style.transform  = "translateY(-5px)";
  };

  const handleMouseLeave = (e) => {
    if (screenSize.isMobile) return;
    e.currentTarget.style.boxShadow = "none";
    e.currentTarget.style.transform = "translateY(0)";
  };

  // ── Render ─────────────────────────────────────────────────
  return (
    <div style={styles.container}>
      {/* Header */}
      <div
        style={{ ...styles.card, display: "flex", gap: "10px", alignItems: "center" }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <Calculator size={26} />
        <h1 style={styles.title}>{t.salaryCalculator}</h1>
      </div>

      {/* Processing-mode selector (Individual / Bulk) */}
      <div style={styles.card}>
        <ModeSelector
          processingMode={processingMode}
          onSelectMode={handleSelectProcessingMode}
          styles={styles}
          t={t}
        />
      </div>

      {/* Calculation-mode toggle (Net←Gross / Gross←Net) */}
      <CalculationModeToggle
        calculationMode={calculationMode}
        processingMode={processingMode}
        onSelectMode={handleSelectCalculationMode}
        styles={styles}
        t={t}
      />

      {/* ── Individual mode ───────────────────────────────── */}
      {processingMode === "individual" && (
        <>
          <IndividualForm
            calculationMode={calculationMode}
            inputs={individual.inputs}
            errors={individual.errors}
            onInputChange={individual.handleInputChange}
            onCalculate={individual.handleCalculate}
            onReset={individual.handleReset}
            styles={styles}
            t={t}
          />

          {individual.results && (
            <ResultsCard
              results={individual.results}
              styles={styles}
              t={t}
              theme={theme}
              currentTheme={currentTheme}
              isRTL={isRTL}
            />
          )}
        </>
      )}

      {/* ── Bulk Excel mode ───────────────────────────────── */}
      {processingMode === "bulk" && (
        <BulkUploader
          file={bulk.file}
          processing={bulk.processing}
          processedData={bulk.processedData}
          bulkError={bulk.bulkError}
          success={bulk.success}
          onFileUpload={bulk.handleFileUpload}
          onProcess={bulk.processExcel}
          onDownload={bulk.downloadExcel}
          styles={styles}
          t={t}
          screenSize={screenSize}
          currentTheme={currentTheme}
        />
      )}

      {/* Global animation / scrollbar styles */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .upload-area:hover {
          background: ${theme === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)"} !important;
          border-color: #2e7cba !important;
          transform: scale(1.01);
        }
        input:focus {
          border-color: #2e7cba !important;
          box-shadow: 0 0 0 3px rgba(59,130,246,0.1);
          transform: scale(1.01);
        }
        button:not(:disabled):hover { transform: translateY(-2px) scale(1.02); }
        button:active               { transform: translateY(0) scale(0.98); }
        button:disabled             { cursor: not-allowed; opacity: 0.5; }
        ::-webkit-scrollbar              { width: 10px; height: 10px; }
        ::-webkit-scrollbar-track        { background: ${currentTheme.colors.scrollbar?.track || (theme === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)")}; border-radius: 10px; }
        ::-webkit-scrollbar-thumb        { background: linear-gradient(135deg, #2e7cba, #8b5cf6); border-radius: 10px; }
        ::-webkit-scrollbar-thumb:hover  { background: linear-gradient(135deg, #2563eb, #7c3aed); }
        *               { box-sizing: border-box; }
        *:focus-visible { outline: 2px solid #2e7cba; outline-offset: 2px; }
        ::selection     { background: rgba(59,130,246,0.2); color: ${currentTheme.colors.textPrimary}; }
        @media (max-width: 768px) {
          .mode-switch, .calc-mode-toggle { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
};

export default TaxCalculatorPage;